import React from "react";
import { Modal } from "rsuite";
import { Translation } from "react-i18next";
import { connect } from "react-redux";
import toast from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";
import {
  FaCrown,
  FaShieldAlt,
  FaLock,
  FaCoins,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
} from "react-icons/fa";
import { GiSparkles, GiCoinsPile } from "react-icons/gi";

import { UserService } from "_services";
import Emitter from "_actions/emitter";
import { getCurrency } from "components/Navbar/NavUserBox";
import i18n from "i18n";

const VIEW_DETAIL = "detail";
const VIEW_DEPOSIT = "deposit";
const VIEW_WITHDRAW = "withdraw";
const VIEW_RESULT = "result";

const AMOUNT_STEP = 5;
const MAX_AMOUNT = 999999;
const TERMINAL_PAYMENT_STATUSES = ["Confirmed", "Failed", "Expired"];
const PAYMENT_STATUS_POLL_MS = 5000;

const ASSET_ICON_MAP = {
  "USDT|tron": require("../../assets/img/coins/usdt_tron.png"),
  "USDT|ethereum": require("../../assets/img/coins/usdt_eth.png"),
  "USDT|bsc": require("../../assets/img/coins/usdt_bsc.png"),
  "USDT|polygon": require("../../assets/img/coins/usdt_polygon.png"),
  "USDT|solana": require("../../assets/img/coins/usdt_solana.png"),
  "BTC|bitcoin": require("../../assets/img/coins/btc.png"),
  "ETH|ethereum": require("../../assets/img/coins/eth.png"),
};

const getAssetIcon = (asset = {}) => {
  const label = (asset.label || "").toUpperCase();
  const network = (asset.network || "").toLowerCase();
  const base = label.startsWith("USDT") ? "USDT" : label;
  return ASSET_ICON_MAP[`${base}|${network}`] || null;
};

// GEÇİCİ: backend şu an sadece USDT/TRC20 için "asset": "USDT", "network": "TRC20"
// sabit değerlerini kabul ediyor; asset listesindeki token/network alanlarıyla
// (ör. "usdttrc20"/"tron") uyuşmuyor. Diğer coinler backend uyumlu hale gelene
// kadar seçilemez bırakıldı.
const isAssetEnabled = (asset) =>
  (asset?.label || "").toUpperCase().startsWith("USDT") &&
  (asset?.network || "").toLowerCase() === "tron";

const TRUST_COINS = ["USDT", "BTC", "ETH"];
const TRUST_ICON_MAP = {
  USDT: ASSET_ICON_MAP["USDT|tron"],
  BTC: ASSET_ICON_MAP["BTC|bitcoin"],
  ETH: ASSET_ICON_MAP["ETH|ethereum"],
};

const GenericCoinIcon = ({ className = "w-9 h-9" }) => (
  <div
    className={`shrink-0 ${className} rounded-full bg-gradient-to-br from-secondary-600 to-secondary-800 border border-primary-400/30 flex items-center justify-center text-white shadow-md`}
  >
    <FaCoins className="w-1/2 h-1/2" />
  </div>
);

// Icon and label are stacked in the same grid cell (col-start-1/row-start-1)
// instead of sharing a flex row or relying on percentage-based absolute
// positioning. The row's height is driven by whichever is tallest (the label,
// once it wraps), and each item is centered independently against that same
// row with self-center/justify-self — no computed-height lookup or
// shrink-to-fit sizing that could drift, so the icon lands on the vertical
// center of the label no matter how many lines it wraps to.
const ButtonContent = ({ label, icon, loading }) => {
  if (loading) {
    return (
      <span className="col-start-1 row-start-1 self-center justify-self-center">
        <span className="loading loading-spinner loading-sm" />
      </span>
    );
  }
  return (
    <>
      {icon && (
        <span className="col-start-1 row-start-1 self-center justify-self-start">
          {icon}
        </span>
      )}
      <span
        className={`col-start-1 row-start-1 self-center text-center leading-tight ${
          icon ? "pl-6" : ""
        }`}
      >
        {label}
      </span>
    </>
  );
};

export const GoldButton = ({ label, onClick, disabled, loading, icon }) => {
  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className="grid content-center h-full w-full rounded-xl py-2.5 px-3 font-bold text-xs uppercase tracking-wide bg-gradient-to-r from-primary-200 via-primary-300 to-primary-400 text-secondary-950 opacity-40 grayscale cursor-not-allowed"
      >
        <ButtonContent label={label} icon={icon} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid content-center h-full w-full rounded-xl py-2.5 px-3 font-bold text-xs uppercase tracking-wide bg-gradient-to-r from-primary-500 via-primary-500 to-primary-500 text-primary-50 shadow-lg shadow-primary-500/10 active:scale-[0.98] transition-transform"
    >
      <ButtonContent label={label} icon={icon} loading={loading} />
    </button>
  );
};

const OutlineGoldButton = ({ label, onClick, disabled, loading, icon }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`grid content-center h-full w-full rounded-xl py-2.5 px-3 font-bold text-xs uppercase tracking-wide border transition-transform ${
      disabled
        ? "border-primary-400/60 text-white opacity-40 grayscale cursor-not-allowed"
        : "border-primary-400/60 text-white hover:bg-primary-400/10 active:scale-[0.98]"
    }`}
  >
    <ButtonContent label={label} icon={icon} loading={loading} />
  </button>
);

export const CloseButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={i18n.t("BALANCE_MODAL_CLOSE")}
    className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full border border-secondary-50 bg-secondary-50/20 text-white flex items-center justify-center hover:bg-secondary-700 transition-colors"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth={2}
      stroke="currentColor"
      className="w-4 h-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
);

export const DividerDiamond = () => (
  <div className="w-full flex items-center gap-3 my-5">
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-50/40 to-transparent" />
    <div className="w-2 h-2 rotate-45 bg-primary-50" />
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-50/40 to-transparent" />
  </div>
);

const BalanceModal = (props) => {
  const [view, setView] = React.useState(VIEW_DETAIL);
  const [assets, setAssets] = React.useState([]);
  const [assetsLoading, setAssetsLoading] = React.useState(false);
  const [selectedAsset, setSelectedAsset] = React.useState(null);
  const [amount, setAmount] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [createLoading, setCreateLoading] = React.useState(false);
  const [payment, setPayment] = React.useState(null);
  const [paymentStatus, setPaymentStatus] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [withdrawAmount, setWithdrawAmount] = React.useState("");
  const [withdrawLoading, setWithdrawLoading] = React.useState(false);
  const [withdrawSubmitted, setWithdrawSubmitted] = React.useState(false);

  const resetState = () => {
    setView(VIEW_DETAIL);
    setSelectedAsset(null);
    setAmount("");
    setPreview(null);
    setPreviewLoading(false);
    setCreateLoading(false);
    setPayment(null);
    setPaymentStatus(null);
    setCopied(false);
    setWithdrawAmount("");
    setWithdrawLoading(false);
    setWithdrawSubmitted(false);
  };

  React.useEffect(() => {
    if (!props.isActive) {
      resetState();
      return;
    }
    if (props.initialView === VIEW_DEPOSIT) {
      setView(VIEW_DEPOSIT);
      if (!assets.length) {
        loadAssets();
      }
    } else if (props.initialView === VIEW_WITHDRAW) {
      setView(VIEW_WITHDRAW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isActive]);

  React.useEffect(() => {
    document.body.classList.toggle("chatwoot-force-hidden", !!props.isActive);
    return () => {
      document.body.classList.remove("chatwoot-force-hidden");
    };
  }, [props.isActive]);

  const loadAssets = () => {
    setAssetsLoading(true);
    UserService.GET_ASSETS()
      .then((res) => {
        setAssets(res?.assets || []);
        setAssetsLoading(false);
      })
      .catch((err) => {
        toast.error(i18n.t("BALANCE_MODAL_LOAD_ERROR"));
        setAssetsLoading(false);
      });
  };

  const goToDeposit = () => {
    setView(VIEW_DEPOSIT);
    if (!assets.length) {
      loadAssets();
    }
  };

  const handleSelectAsset = (asset) => {
    if (!isAssetEnabled(asset)) return;
    setSelectedAsset(asset);
  };

  const handleAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = value.indexOf(".");
    if (firstDot !== -1) {
      value =
        value.slice(0, firstDot + 1) +
        value.slice(firstDot + 1).replace(/\./g, "");
    }
    if (parseFloat(value) > MAX_AMOUNT) {
      value = String(MAX_AMOUNT);
    }
    setAmount(value);
  };

  const handleStep = (delta) => {
    const current = parseFloat(amount) || 0;
    const next = Math.min(MAX_AMOUNT, Math.max(0, current + delta));
    setAmount(Number.isInteger(next) ? String(next) : next.toFixed(2));
  };

  const numericAmount = parseFloat(amount) || 0;

  React.useEffect(() => {
    if (!selectedAsset || numericAmount <= 0) {
      setPreview(null);
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    const handle = setTimeout(() => {
      UserService.GET_PREVIEW_USD(numericAmount)
        .then((res) => {
          setPreview(res);
          setPreviewLoading(false);
        })
        .catch(() => {
          setPreview(null);
          setPreviewLoading(false);
        });
    }, 450);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset, numericAmount]);

  // Açık ödemesi olan kullanıcı yeni ödeme oluşturamaz; backend 429 ile
  // mevcut ödemenin bilgisini döner, kullanıcıyı o ödemenin sonuç ekranına yönlendiriyoruz.
  React.useEffect(() => {
    if (view !== VIEW_RESULT || !payment?.paymentId) return;
    if (TERMINAL_PAYMENT_STATUSES.includes(paymentStatus)) return;
    let stopped = false;
    let timeoutId;
    const poll = () => {
      UserService.GET_PAYMENT_STATUS(payment.paymentId)
        .then((res) => {
          if (stopped) return;
          setPayment((prev) => (prev ? { ...prev, ...res } : res));
          setPaymentStatus(res?.status || null);
          if (!TERMINAL_PAYMENT_STATUSES.includes(res?.status)) {
            timeoutId = setTimeout(poll, PAYMENT_STATUS_POLL_MS);
          }
        })
        .catch(() => {
          if (!stopped) timeoutId = setTimeout(poll, PAYMENT_STATUS_POLL_MS);
        });
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, payment?.paymentId]);

  const handleCreatePayment = () => {
    setCreateLoading(true);
    // GEÇİCİ: backend sadece "USDT"/"TRC20" sabit değerlerini kabul ediyor,
    // bu yüzden seçili asset'in token/network alanları yerine bunlar gönderiliyor.
    UserService.CREATE_PAYMENT(numericAmount, "USDT", "TRC20")
      .then((res) => {
        setPaymentStatus(null);
        setPayment(res);
        setView(VIEW_RESULT);
        setCreateLoading(false);
      })
      .catch((err) => {
        setCreateLoading(false);
        if (err?.response?.status === 429) {
          const data = err.response.data?.dynamicValue || {};
          const failText = err.response.data?.failText?.[0];
          toast.error(failText || i18n.t("BALANCE_MODAL_PAYMENT_IN_PROGRESS"));
          if (data.existingPaymentId && data.existingCryptoAddress) {
            setPaymentStatus(null);
            setPayment({
              paymentId: data.existingPaymentId,
              cryptoAddress: data.existingCryptoAddress,
              hostedUrl: null,
            });
            setView(VIEW_RESULT);
          }
          return;
        }
        toast.error(i18n.t("BALANCE_MODAL_PAYMENT_ERROR"));
      });
  };

  const handleCopyAddress = () => {
    if (!payment?.cryptoAddress) return;
    navigator.clipboard?.writeText(payment.cryptoAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleClose = () => {
    Emitter.emit("BALANCE_MODAL", false);
  };

  const balance = props?.wallet?.main ?? 0;
  const bonus = props?.wallet?.bonus ?? 0;

  const currency = getCurrency(props?.user?.Currency);
  const canCreatePayment = !!(
    preview &&
    !previewLoading &&
    selectedAsset &&
    numericAmount > 0
  );

  const goToWithdraw = () => {
    setWithdrawAmount("");
    setWithdrawSubmitted(false);
    setView(VIEW_WITHDRAW);
  };

  const handleWithdrawAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = value.indexOf(".");
    if (firstDot !== -1) {
      value =
        value.slice(0, firstDot + 1) +
        value.slice(firstDot + 1).replace(/\./g, "");
    }
    const cap = Math.min(MAX_AMOUNT, balance);
    if (parseFloat(value) > cap) {
      value = String(cap);
    }
    setWithdrawAmount(value);
  };

  const handleWithdrawStep = (delta) => {
    const current = parseFloat(withdrawAmount) || 0;
    const cap = Math.min(MAX_AMOUNT, balance);
    const next = Math.min(cap, Math.max(0, current + delta));
    setWithdrawAmount(Number.isInteger(next) ? String(next) : next.toFixed(2));
  };

  const numericWithdrawAmount = parseFloat(withdrawAmount) || 0;
  const canCreateWithdraw =
    numericWithdrawAmount > 0 &&
    numericWithdrawAmount <= balance &&
    !withdrawLoading;

  const handleCreateWithdrawRequest = () => {
    if (!canCreateWithdraw) return;
    setWithdrawLoading(true);
    UserService.CREATE_WITHDRAW_REQUEST(numericWithdrawAmount)
      .then(() => {
        setWithdrawLoading(false);
        setWithdrawSubmitted(true);
        Emitter.emit("REFRESH_WALLET");
        Emitter.emit("WITHDRAW_REQUEST_CREATED");
      })
      .catch((err) => {
        setWithdrawLoading(false);
        const failText = err?.response?.data?.failText?.[0];
        toast.error(failText || i18n.t("BALANCE_MODAL_WITHDRAW_ERROR"));
      });
  };

  const renderDetail = (t) => (
    <div className="flex flex-col items-center px-3 pb-4 pt-2">
      <div className="relative flex items-center justify-center w-28 h-28 mb-3">
        <div className="absolute inset-0 rounded-full bg-primary-400/25 blur-2xl" />
        <div className="absolute inset-0 rounded-full border border-secondary-50/60" />
        <div className="relative flex items-center justify-center w-[4.5rem] h-[4.5rem] rounded-full bg-gradient-to-brfrom-primary-500 via-primary-500 to-primary-500 shadow-lg shadow-secondary-400/50">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-8 h-8 text-white"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"
            />
          </svg>
        </div>
      </div>

      <span className="text-secondary-50 text-xs font-semibold uppercase tracking-[0.2em]">
        {t("BALANCE_MODAL_AVAILABLE_BALANCE")}
      </span>
      <span className="mt-1 flex items-baseline gap-2 flex-wrap justify-center text-center px-2">
        <span className="bg-gradient-to-b from-white to-primary-100 bg-clip-text text-transparent text-4xl font-extrabold tracking-tight whitespace-nowrap">
          {balance.toLocaleString("tr-TR", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
        <span className="text-2xl text-white font-semibold whitespace-nowrap">
          {currency}
        </span>
      </span>

      {bonus > 0 && (
        <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 max-w-full flex-wrap justify-center">
          <GiSparkles className="text-emerald-400 text-xs shrink-0" />
          <span className="text-emerald-300 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap">
            {t("BALANCE_MODAL_BONUS_BALANCE")}
          </span>
          <span className="text-emerald-300 text-xs font-bold whitespace-nowrap">
            {bonus.toLocaleString("tr-TR", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}{" "}
            {currency}
          </span>
        </span>
      )}

      <div className="w-full mt-2">
        <DividerDiamond />
      </div>

      <div className="w-full flex gap-2">
        <div className="flex-1">
          <GoldButton
            label={t("BALANCE_MODAL_DEPOSIT_BUTTON")}
            onClick={goToDeposit}
            icon={
              <span className="w-4 h-4 rounded-full border-2 border-secondary-950/70 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth={3}
                  stroke="currentColor"
                  className="w-2.5 h-2.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
              </span>
            }
          />
        </div>
        <div className="flex-1">
          <OutlineGoldButton
            label={t("BALANCE_MODAL_WITHDRAW_BUTTON")}
            onClick={goToWithdraw}
            disabled={balance <= 0}
            icon={
              <span className="w-4 h-4 rounded-full border-2 border-primary-400/70 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth={3}
                  stroke="currentColor"
                  className="w-2.5 h-2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15" />
                </svg>
              </span>
            }
          />
        </div>
      </div>

      <div className="w-full flex items-center gap-3 mt-6 mb-3">
        <div className="flex-1 h-px bg-secondary-700" />
        <span className="text-secondary-50 text-[11px] uppercase tracking-[0.2em]">
          {t("BALANCE_MODAL_ACCEPTED_COINS")}
        </span>
        <div className="flex-1 h-px bg-secondary-700" />
      </div>

      <div className="flex items-center justify-center gap-5">
        {TRUST_COINS.map((sym) => (
          <div key={sym} className="flex flex-col items-center gap-1.5">
            <img
              src={TRUST_ICON_MAP[sym]}
              alt={sym}
              className="w-11 h-11 object-contain"
            />
            <span className="text-white text-[10px] font-medium">
              {sym}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 mt-5 text-white text-[11px]">
        <FaShieldAlt className="text-white/80" />
        <span>{t("BALANCE_MODAL_TRUST_TEXT")}</span>
      </div>
    </div>
  );

  const renderDeposit = (t) => (
    <div className="flex flex-col px-1 py-1">
      <div className="flex flex-col items-center mb-4">
        <FaCrown className="text-white text-lg mb-1" />
        <div className="flex items-center gap-2">
          <GiSparkles className="text-secondary-50/60 text-xs" />
          <span className="bg-gradient-to-b from-primary-200 to-secondary-50 bg-clip-text text-transparent text-lg font-extrabold uppercase tracking-wide">
            {t("BALANCE_MODAL_DEPOSIT_BUTTON")}
          </span>
          <GiSparkles className="text-secondary-50/60 text-xs" />
        </div>
        <span className="text-secondary-300 text-[11px] uppercase tracking-[0.15em] mt-1">
          {t("BALANCE_MODAL_CHOOSE_COIN")}
        </span>
      </div>

      {assetsLoading ? (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner text-white" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 mb-4 px-2">
          {assets.map((asset, idx) => {
            const isSelected =
              selectedAsset?.token === asset.token &&
              selectedAsset?.network === asset.network;
            const enabled = isAssetEnabled(asset);
            const icon = getAssetIcon(asset);
            return (
              <div
                key={`${asset.token}-${asset.network}-${idx}`}
                onClick={() => handleSelectAsset(asset)}
                className={`relative rounded-2xl border px-3 py-3 flex items-center gap-3 transition-all duration-150 ${
                  !enabled
                    ? "cursor-not-allowed opacity-40 grayscale border-secondary-700 bg-secondary-600/20"
                    : isSelected
                      ? "cursor-pointer border-primary-400 bg-secondary-700/80 shadow-lg shadow-secondary-400/30 scale-[1.02]"
                      : "cursor-pointer border-secondary-700 bg-secondary-600/30 hover:bg-secondary-600/50"
                }`}
              >
                {icon ? (
                  <img
                    src={icon}
                    alt={asset.label}
                    className="shrink-0 w-10 h-10 object-contain"
                  />
                ) : (
                  <GenericCoinIcon className="w-10 h-10" />
                )}
                <div className="flex flex-col min-w-0">
                  <span className="text-white text-sm font-semibold truncate">
                    {asset.label}
                  </span>
                  <span className="text-secondary-300 text-[11px] capitalize truncate">
                    {asset.network}
                  </span>
                </div>
                {isSelected && (
                  <div className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center ring-2 ring-secondary-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth={3}
                      stroke="white"
                      className="w-3 h-3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  </div>
                )}
                {!enabled && (
                  <span className="absolute -top-1.5 -end-1.5 rounded-full bg-secondary-800 border border-secondary-600 px-1.5 py-0.5 text-[9px] text-secondary-300 uppercase tracking-wide">
                    {
                      //t("BALANCE_MODAL_COMING_SOON")
                    }
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <span className="text-white text-xs uppercase tracking-[0.15em] mb-2">
        {t("BALANCE_MODAL_AMOUNT_USD")}
      </span>
      <div className="flex items-stretch gap-2 mb-3">
        <div className="flex-1 flex items-center rounded-xl border border-primary-400/70 bg-secondary-700/40 overflow-hidden">
          <button
            type="button"
            onClick={() => handleStep(-AMOUNT_STEP)}
            className="w-11 h-12 shrink-0 flex items-center justify-center text-white text-xl font-bold hover:bg-secondary-700/60"
          >
            −
          </button>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            maxLength={9}
            className="flex-1 min-w-0 bg-transparent text-center text-white text-xl font-bold outline-none"
          />
          <button
            type="button"
            onClick={() => handleStep(AMOUNT_STEP)}
            className="w-11 h-12 shrink-0 flex items-center justify-center text-white text-xl font-bold hover:bg-secondary-700/60"
          >
            +
          </button>
        </div>
        <div className="w-16 shrink-0 flex items-center justify-center rounded-xl border border-primary-400 bg-primary-500/40 text-white text-sm font-semibold">
          USD
        </div>
      </div>

      {selectedAsset && numericAmount > 0 && (
        <div className="rounded-2xl border border-primary-400 bg-secondary-300/30 px-4 py-3 mb-4 flex items-center gap-3">
          <div className="flex-1 flex flex-col gap-1.5 min-w-0">
            <div className="flex justify-between text-sm">
              <span className="text-white">
                {t("BALANCE_MODAL_YOU_SEND")}
              </span>
              <span className="text-white">{amount} USD</span>
            </div>
            <div className="flex justify-between text-sm items-center">
              <span className="text-white">
                {t("BALANCE_MODAL_YOU_RECEIVE")}
              </span>
              {previewLoading ? (
                <span className="loading loading-spinner loading-xs text-white" />
              ) : (
                <span className="text-white font-semibold">
                  {preview?.amountInCurrency} {preview?.currency}
                </span>
              )}
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-white">
                {t("BALANCE_MODAL_RATE")}
              </span>
              <span className="text-secondary-50">{preview?.rate ?? "-"}</span>
            </div>
          </div>
          <GiCoinsPile className="w-9 h-9 text-white shrink-0" />
        </div>
      )}

      <GoldButton
        label={t("BALANCE_MODAL_CREATE_PAYMENT")}
        onClick={handleCreatePayment}
        disabled={!canCreatePayment}
        loading={createLoading}
      />

      <div className="flex items-center justify-center gap-1.5 mt-3 text-white/70 text-[11px]">
        <FaLock className="text-white/70" />
        <span>{t("BALANCE_MODAL_SECURE_PAYMENT")}</span>
      </div>
    </div>
  );

  const renderWithdraw = (t) => {
    if (withdrawSubmitted) {
      return (
        <div className="flex flex-col items-center px-2 py-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
            <FaCheckCircle className="text-emerald-400 text-5xl" />
          </div>
          <span className="text-white text-lg font-extrabold uppercase tracking-wide mb-2 text-center">
            {t("BALANCE_MODAL_WITHDRAW_SUCCESS_TITLE")}
          </span>
          <span className="text-secondary-300 text-sm text-center">
            {t("BALANCE_MODAL_WITHDRAW_SUCCESS_TEXT", {
              amount: numericWithdrawAmount.toLocaleString("tr-TR", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }),
              currency,
            })}
          </span>
          <div className="w-full mt-8">
            <GoldButton label={t("BALANCE_MODAL_DONE")} onClick={handleClose} />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col px-1 py-1">
        <div className="flex flex-col items-center mb-4">
          <FaMoneyBillWave className="text-white text-lg mb-1" />
          <div className="flex items-center gap-2">
            <GiSparkles className="text-secondary-50/60 text-xs" />
            <span className="bg-gradient-to-b from-primary-200 to-secondary-50 bg-clip-text text-transparent text-lg font-extrabold uppercase tracking-wide">
              {t("BALANCE_MODAL_WITHDRAW_BUTTON")}
            </span>
            <GiSparkles className="text-secondary-50/60 text-xs" />
          </div>
          <span className="text-white text-[11px] uppercase tracking-[0.15em] mt-1 text-center">
            {t("BALANCE_MODAL_WITHDRAW_SUBTITLE")}
          </span>
        </div>

        <div className="w-full mb-2">
          <DividerDiamond />
        </div>

        <div className="flex items-center justify-between gap-2 rounded-xl border border-secondary-300 bg-secondary-500 px-3.5 py-2.5 mb-4">
          <span className="text-white text-xs uppercase tracking-[0.1em] shrink-0">
            {t("BALANCE_MODAL_WITHDRAW_AVAILABLE")}
          </span>
          <span className="text-white text-sm font-bold text-right min-w-0">
            {balance.toLocaleString("tr-TR", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}{" "}
            {currency}
          </span>
        </div>

        <span className="text-white text-xs uppercase tracking-[0.15em] mb-2">
          {t("BALANCE_MODAL_WITHDRAW_AMOUNT_LABEL")}
        </span>
        <div className="flex items-stretch gap-2 mb-2">
          <div className="flex-1 flex items-center rounded-xl border border-primary-400/70 bg-secondary-700/40 overflow-hidden">
            <button
              type="button"
              onClick={() => handleWithdrawStep(-AMOUNT_STEP)}
              className="w-11 h-12 shrink-0 flex items-center justify-center text-white text-xl font-bold hover:bg-secondary-700/60"
            >
              −
            </button>
            <input
              type="text"
              inputMode="decimal"
              value={withdrawAmount}
              onChange={handleWithdrawAmountChange}
              placeholder="0.00"
              maxLength={9}
              className="flex-1 min-w-0 bg-transparent text-center text-white text-xl font-bold outline-none"
            />
            <button
              type="button"
              onClick={() => handleWithdrawStep(AMOUNT_STEP)}
              className="w-11 h-12 shrink-0 flex items-center justify-center text-white text-xl font-bold hover:bg-secondary-700/60"
            >
              +
            </button>
          </div>
          <div className="w-16 shrink-0 flex items-center justify-center rounded-xl border border-primary-400/70 bg-secondary-700/40 text-white text-sm font-semibold">
            {currency}
          </div>
        </div>

        {balance > 0 && (
          <button
            type="button"
            onClick={() => setWithdrawAmount(String(balance))}
            className="self-end text-secondary-50 text-[11px] uppercase tracking-wide font-semibold mb-4 hover:text-primary-300"
          >
            {t("BALANCE_MODAL_WITHDRAW_MAX")}
          </button>
        )}

        <GoldButton
          label={t("BALANCE_MODAL_WITHDRAW_SUBMIT")}
          onClick={handleCreateWithdrawRequest}
          disabled={!canCreateWithdraw}
          loading={withdrawLoading}
        />

        <div className="flex items-center justify-center gap-1.5 mt-3 text-white text-[11px] text-center">
          <FaLock className="text-white/70 shrink-0" />
          <span>{t("BALANCE_MODAL_WITHDRAW_NOTE")}</span>
        </div>
      </div>
    );
  };

  const renderResult = (t) => {
    const isConfirmed = paymentStatus === "Confirmed";
    const isFailed = paymentStatus === "Failed" || paymentStatus === "Expired";

    if (isConfirmed) {
      return (
        <div className="flex flex-col items-center px-2 py-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
            <FaCheckCircle className="text-emerald-400 text-5xl" />
          </div>
          <span className="text-white text-lg font-extrabold uppercase tracking-wide mb-2 text-center">
            {t("BALANCE_MODAL_STATUS_CONFIRMED_TITLE")}
          </span>
          <span className="text-secondary-300 text-sm text-center">
            {t("BALANCE_MODAL_STATUS_CONFIRMED_TEXT", {
              amount: payment?.creditedAmount ?? payment?.creditAmountExpected,
              currency: payment?.creditCurrency,
            })}
          </span>
          <div className="w-full mt-8">
            <GoldButton label={t("BALANCE_MODAL_DONE")} onClick={handleClose} />
          </div>
        </div>
      );
    }

    if (isFailed) {
      return (
        <div className="flex flex-col items-center px-2 py-6">
          <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mb-4">
            <FaTimesCircle className="text-red-400 text-5xl" />
          </div>
          <span className="text-white text-lg font-extrabold uppercase tracking-wide mb-2 text-center">
            {paymentStatus === "Expired"
              ? t("BALANCE_MODAL_STATUS_EXPIRED_TITLE")
              : t("BALANCE_MODAL_STATUS_FAILED_TITLE")}
          </span>
          <span className="text-secondary-300 text-sm text-center">
            {t("BALANCE_MODAL_STATUS_FAILED_TEXT")}
          </span>
          <div className="w-full mt-8">
            <GoldButton label={t("BALANCE_MODAL_DONE")} onClick={handleClose} />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center px-2 py-4">
        <span className="bg-gradient-to-b from-primary-200 to-secondary-50 bg-clip-text text-transparent text-lg font-extrabold uppercase tracking-wide mb-2">
          {t("BALANCE_MODAL_PAYMENT_ADDRESS")}
        </span>

        <div className="flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-secondary-700/60 border border-primary-400/20">
          <span className="loading loading-spinner loading-xs text-white" />
          <span className="text-white text-[11px] uppercase tracking-wide font-medium">
            {t("BALANCE_MODAL_STATUS_PENDING")}
          </span>
        </div>

        <div className="rounded-2xl border border-primary-400/40 bg-white p-3 shadow-lg shadow-secondary-400/30">
          <QRCodeSVG value={payment?.cryptoAddress || ""} size={180} />
        </div>

        <div
          onClick={handleCopyAddress}
          className="mt-4 w-full rounded-xl border border-secondary-700 bg-secondary-600/40 px-3 py-2 cursor-pointer break-all text-center text-sm text-white hover:bg-secondary-600/60 transition-colors"
          title={t("BALANCE_MODAL_COPY_HINT")}
        >
          {payment?.cryptoAddress}
        </div>
        {copied && (
          <span className="text-white text-xs mt-1">
            {t("BALANCE_MODAL_COPIED")}
          </span>
        )}

        <div className="w-full mt-8">
          <GoldButton label={t("BALANCE_MODAL_DONE")} onClick={handleClose} />
        </div>
      </div>
    );
  };

  return (
    <Translation>
      {(t) => (
        <Modal
          onClose={handleClose}
          backdrop={true}
          className="custom-modal custom-modal-gold"
          open={props.isActive}
          size="xs"
        >
          <Modal.Header className="bg-secondary-500 border-0">
            <Modal.Title className="text-white" />
          </Modal.Header>
          <Modal.Body
            className="relative bg-secondary-500/95"
            style={{
              paddingTop: "0.5em",
              paddingBottom: "1em",
              marginTop: 0,
              maxHeight: "80dvh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <CloseButton onClick={handleClose} />
            {view === VIEW_DETAIL && renderDetail(t)}
            {view === VIEW_DEPOSIT && renderDeposit(t)}
            {view === VIEW_WITHDRAW && renderWithdraw(t)}
            {view === VIEW_RESULT && renderResult(t)}
          </Modal.Body>
        </Modal>
      )}
    </Translation>
  );
};

const mapStateToProps = (state) => {
  const { authentication } = state;
  const { user, wallet } = authentication;
  return {
    user,
    wallet,
  };
};

export default connect(mapStateToProps)(BalanceModal);
